import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChannelCleanerApplyInput, CountryCodesApplyInput, CountryCodesApplyResult, CountryCodesContact, CountryCodesScanResult, DuplicatePhonesApplyInput, DuplicatePhonesApplyResult, DuplicatePhonesContact, DuplicatePhonesScanResult, ChannelCleanerApplyResult, ChannelCleanerContact, ChannelCleanerScanResult, NoContactInfoFields, NoContactInfoItem, Paginated, NameCleanupApplyInput, NameCleanupBatchResult, NameCleanupInput, NameCleanupPreview, OddContact, OddContactsScanResult } from '@crm/shared';
import { api } from '../../api/client';

export function usePreviewNameCleanup() {
  return useMutation({ mutationFn: async (input: NameCleanupInput) => (await api.post<NameCleanupPreview>('/cleanup/names/preview', input)).data });
}

export interface ApplyProgress {
  target: 'contacts' | 'organizations';
  scanned: number;
  updated: number;
}

/** Runs the clean up batch by batch (contacts, then organizations) so no single request runs long. */
export function useApplyNameCleanup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ input, onProgress }: { input: NameCleanupInput; onProgress?: (p: ApplyProgress) => void }) => {
      const totals = { contacts: 0, organizations: 0 };
      for (const target of ['contacts', 'organizations'] as const) {
        let after: string | null = null;
        let scanned = 0;
        do {
          const body: NameCleanupApplyInput = { ...input, target, after };
          const r: NameCleanupBatchResult = (await api.post<NameCleanupBatchResult>('/cleanup/names/apply', body)).data;
          scanned += r.scanned;
          totals[target] += r.updated;
          onProgress?.({ target, scanned, updated: totals[target] });
          after = r.next;
        } while (after);
      }
      return totals;
    },
    onSuccess: () => ['contacts', 'contact', 'organizations', 'organization', 'deals', 'deal'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
}

export interface OddContactsState {
  results: OddContact[];
  scanned: number;
  finishedAt: string;
}

const ODD_KEY = ['odd-contacts-scan'];

/** The last scan's results, kept in the query cache so opening a contact and coming back keeps them. */
export function useOddContactsResults() {
  const qc = useQueryClient();
  const query = useQuery<OddContactsState | null>({ queryKey: ODD_KEY, queryFn: () => qc.getQueryData<OddContactsState>(ODD_KEY) ?? null, enabled: false, staleTime: Infinity, gcTime: 60 * 60_000 });
  return { data: query.data ?? null, set: (v: OddContactsState | null) => qc.setQueryData(ODD_KEY, v) };
}

/** Scans every contact batch by batch; `onProgress` gets the running totals. */
export function useScanOddContacts() {
  return useMutation({
    mutationFn: async ({ onProgress }: { onProgress?: (p: { scanned: number; total: number; found: number }) => void }) => {
      const results: OddContact[] = [];
      let after: string | null = null;
      let scanned = 0;
      let total = 0;
      do {
        const r: OddContactsScanResult = (await api.post<OddContactsScanResult>('/cleanup/odd-contacts/scan', { after })).data;
        total = r.total ?? total;
        scanned += r.scanned;
        results.push(...r.results);
        onProgress?.({ scanned, total, found: results.length });
        after = r.next;
      } while (after);
      return { results, scanned, finishedAt: new Date().toISOString() } satisfies OddContactsState;
    },
  });
}

/** Re-checks a few contacts; ones that are fine now (or were deleted) are simply missing from the answer. */
export function useRecheckOddContacts() {
  return useMutation({ mutationFn: async (ids: string[]) => (await api.post<OddContactsScanResult>('/cleanup/odd-contacts/scan', { ids })).data.results });
}

export function useNoContactInfo(params: { page: number; pageSize: number; q?: string }) {
  return useQuery({
    queryKey: ['no-contact-info', params],
    queryFn: async () => (await api.get<Paginated<NoContactInfoItem>>('/cleanup/no-contact-info', { params })).data,
    placeholderData: keepPreviousData,
  });
}

/** Which fields count as contact info on the No contact info page. */
export function useNoContactInfoFields() {
  return useQuery({ queryKey: ['no-contact-info-fields'], queryFn: async () => (await api.get<NoContactInfoFields>('/cleanup/no-contact-info/fields')).data });
}

/** Saves the field choice (`null` restores the default) and reloads the list. */
export function useSaveNoContactInfoFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: string[] | null) => (await api.put<NoContactInfoFields>('/cleanup/no-contact-info/fields', { fields })).data,
    onSuccess: (data) => {
      qc.setQueryData(['no-contact-info-fields'], data);
      void qc.invalidateQueries({ queryKey: ['no-contact-info'] });
    },
  });
}

/** Deletes every contact without contact information. */
export function useDeleteNoContactInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<{ deleted: number }>('/cleanup/no-contact-info/delete')).data,
    onSuccess: () => ['no-contact-info', 'contacts', 'contact', 'dashboard'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
}

export interface ChannelCleanerState {
  results: ChannelCleanerContact[];
  scanned: number;
  finishedAt: string;
}

const CHANNELS_KEY = ['channel-cleaner-scan'];

/** The last Email & Phone Cleaner scan, kept in the query cache like the odd contacts results. */
export function useChannelCleanerResults() {
  const qc = useQueryClient();
  const query = useQuery<ChannelCleanerState | null>({ queryKey: CHANNELS_KEY, queryFn: () => qc.getQueryData<ChannelCleanerState>(CHANNELS_KEY) ?? null, enabled: false, staleTime: Infinity, gcTime: 60 * 60_000 });
  return { data: query.data ?? null, set: (v: ChannelCleanerState | null) => qc.setQueryData(CHANNELS_KEY, v) };
}

export function useScanChannels() {
  return useMutation({
    mutationFn: async ({ onProgress }: { onProgress?: (p: { scanned: number; total: number; found: number }) => void }) => {
      const results: ChannelCleanerContact[] = [];
      let after: string | null = null;
      let scanned = 0;
      let total = 0;
      do {
        const r: ChannelCleanerScanResult = (await api.post<ChannelCleanerScanResult>('/cleanup/channels/scan', { after })).data;
        total = r.total ?? total;
        scanned += r.scanned;
        results.push(...r.results);
        onProgress?.({ scanned, total, found: results.length });
        after = r.next;
      } while (after);
      return { results, scanned, finishedAt: new Date().toISOString() } satisfies ChannelCleanerState;
    },
  });
}

export function useRecheckChannels() {
  return useMutation({ mutationFn: async (ids: string[]) => (await api.post<ChannelCleanerScanResult>('/cleanup/channels/scan', { ids })).data.results });
}

/** Moves the chosen values 500 at a time; `onProgress` gets how many items are done. */
export function useApplyChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ items, removeFromSource, onProgress }: ChannelCleanerApplyInput & { onProgress?: (done: number) => void }) => {
      const total: ChannelCleanerApplyResult = { contacts: 0, emails: 0, phones: 0, merged: 0, skipped: [] };
      for (let i = 0; i < items.length; i += 500) {
        const r = (await api.post<ChannelCleanerApplyResult>('/cleanup/channels/apply', { items: items.slice(i, i + 500), removeFromSource })).data;
        total.contacts += r.contacts;
        total.emails += r.emails;
        total.phones += r.phones;
        total.merged += r.merged;
        total.skipped.push(...r.skipped);
        onProgress?.(Math.min(items.length, i + 500));
      }
      return total;
    },
    onSuccess: () => ['contacts', 'contact', 'odd-contacts-scan', 'no-contact-info'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
}

export interface DuplicatePhonesState {
  results: DuplicatePhonesContact[];
  scanned: number;
  finishedAt: string;
}

const DUP_PHONES_KEY = ['duplicate-phones-scan'];

/** The last Duplicate Phones scan, kept in the query cache like the other clean up results. */
export function useDuplicatePhonesResults() {
  const qc = useQueryClient();
  const query = useQuery<DuplicatePhonesState | null>({ queryKey: DUP_PHONES_KEY, queryFn: () => qc.getQueryData<DuplicatePhonesState>(DUP_PHONES_KEY) ?? null, enabled: false, staleTime: Infinity, gcTime: 60 * 60_000 });
  return { data: query.data ?? null, set: (v: DuplicatePhonesState | null) => qc.setQueryData(DUP_PHONES_KEY, v) };
}

export function useScanDuplicatePhones() {
  return useMutation({
    mutationFn: async ({ onProgress }: { onProgress?: (p: { scanned: number; total: number; found: number }) => void }) => {
      const results: DuplicatePhonesContact[] = [];
      let after: string | null = null;
      let scanned = 0;
      let total = 0;
      do {
        const r: DuplicatePhonesScanResult = (await api.post<DuplicatePhonesScanResult>('/cleanup/duplicate-phones/scan', { after })).data;
        total = r.total ?? total;
        scanned += r.scanned;
        results.push(...r.results);
        onProgress?.({ scanned, total, found: results.length });
        after = r.next;
      } while (after);
      return { results, scanned, finishedAt: new Date().toISOString() } satisfies DuplicatePhonesState;
    },
  });
}

/** Removes the duplicates of the given contacts 500 at a time; `onProgress` gets how many contacts are done. */
export function useApplyDuplicatePhones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ contactIds, onProgress }: DuplicatePhonesApplyInput & { onProgress?: (done: number) => void }) => {
      const total: DuplicatePhonesApplyResult = { contacts: 0, removed: 0, skipped: [] };
      for (let i = 0; i < contactIds.length; i += 500) {
        const r = (await api.post<DuplicatePhonesApplyResult>('/cleanup/duplicate-phones/apply', { contactIds: contactIds.slice(i, i + 500) })).data;
        total.contacts += r.contacts;
        total.removed += r.removed;
        total.skipped.push(...r.skipped);
        onProgress?.(Math.min(contactIds.length, i + 500));
      }
      return total;
    },
    onSuccess: () => ['contacts', 'contact', 'odd-contacts-scan', 'channel-cleaner-scan'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
}

export interface CountryCodesState {
  results: CountryCodesContact[];
  scanned: number;
  finishedAt: string;
}

const COUNTRY_CODES_KEY = ['country-codes-scan'];

/** The last Country codes scan, kept in the query cache like the other clean up results. */
export function useCountryCodesResults() {
  const qc = useQueryClient();
  const query = useQuery<CountryCodesState | null>({ queryKey: COUNTRY_CODES_KEY, queryFn: () => qc.getQueryData<CountryCodesState>(COUNTRY_CODES_KEY) ?? null, enabled: false, staleTime: Infinity, gcTime: 60 * 60_000 });
  return { data: query.data ?? null, set: (v: CountryCodesState | null) => qc.setQueryData(COUNTRY_CODES_KEY, v) };
}

export function useScanCountryCodes() {
  return useMutation({
    mutationFn: async ({ onProgress }: { onProgress?: (p: { scanned: number; total: number; found: number }) => void }) => {
      const results: CountryCodesContact[] = [];
      let after: string | null = null;
      let scanned = 0;
      let total = 0;
      do {
        const r: CountryCodesScanResult = (await api.post<CountryCodesScanResult>('/cleanup/country-codes/scan', { after })).data;
        total = r.total ?? total;
        scanned += r.scanned;
        results.push(...r.results);
        onProgress?.({ scanned, total, found: results.length });
        after = r.next;
      } while (after);
      return { results, scanned, finishedAt: new Date().toISOString() } satisfies CountryCodesState;
    },
  });
}

export function useRecheckCountryCodes() {
  return useMutation({ mutationFn: async (ids: string[]) => (await api.post<CountryCodesScanResult>('/cleanup/country-codes/scan', { ids })).data.results });
}

/** Fixes or removes the chosen numbers 500 at a time; `onProgress` gets how many items are done. */
export function useApplyCountryCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ items, onProgress }: CountryCodesApplyInput & { onProgress?: (done: number) => void }) => {
      const total: CountryCodesApplyResult = { contacts: 0, fixed: 0, removed: 0, skipped: [] };
      for (let i = 0; i < items.length; i += 500) {
        const r = (await api.post<CountryCodesApplyResult>('/cleanup/country-codes/apply', { items: items.slice(i, i + 500) })).data;
        total.contacts += r.contacts;
        total.fixed += r.fixed;
        total.removed += r.removed;
        total.skipped.push(...r.skipped);
        onProgress?.(Math.min(items.length, i + 500));
      }
      return total;
    },
    onSuccess: () => ['contacts', 'contact', 'duplicate-phones-scan'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
}
